<template>
    <v-dialog :value="value" @input="$emit('input', $event)" max-width="500px">
        <v-card>
            <v-card-title class="headline">MQTT Credentials</v-card-title>
            <v-card-text>
                <div v-if="credentials">
                    <p>
                        <strong>Username:</strong> 
                        <span id="mqtt-user">{{ credentials.user }}</span>
                        <v-btn icon small @click="copyToClipboard('mqtt-user')">
                            <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                    </p>

                    <p>
                        <strong>Password:</strong>
                        <span id="mqtt-pass-display">{{ showPassword ? credentials.pass : '********' }}</span>
                        <v-btn icon small @click="copyToClipboard('mqtt-pass')">
                            <v-icon>mdi-content-copy</v-icon>
                        </v-btn>
                        <v-btn icon small @click="showPassword = !showPassword">
                            <v-icon>{{ showPassword ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                        </v-btn>
                    </p>
                </div>
                <div v-else>
                    <v-progress-circular indeterminate color="primary"></v-progress-circular>
                    <p class="mt-2">Loading credentials...</p>
                </div>

                <v-alert v-if="copySuccess" type="success" dense dismissible class="mt-3">
                    Copied to clipboard!
                </v-alert>
                <v-alert v-if="copyError" type="error" dense dismissible class="mt-3">
                    Failed to copy!
                </v-alert>
            </v-card-text>
            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn color="blue darken-1" text @click="closeDialog">Close</v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script setup>
import { ref, watch } from 'vue';
import axios from '@/plugins/axios';

const props = defineProps({
    value: {
        type: Boolean,
        required: true
    }
});

const emit = defineEmits(['input', 'dialog-closed']);

const credentials = ref(null);
const showPassword = ref(false);
const copySuccess = ref(false);
const copyError = ref(false);

watch(() => props.value, (val) => {
    if (val) {
        getMqttCredentials();
    } else {
        resetState();
    }
});

async function getMqttCredentials() {
    if (!localStorage.userId || !localStorage.sessionToken) {
        credentials.value = { user: 'Error', pass: 'Missing Auth' };
        return;
    }

    const config = { 
        headers: { 
            sessionToken: localStorage.sessionToken, 
            userId: localStorage.userId 
        } 
    };

    try {
        const { data } = await axios.get(
            `/v1/mqttcred?userId=${localStorage.userId}`, 
            config
        );
        credentials.value = data;
    } catch (error) {
        credentials.value = { user: 'Error', pass: 'Could not load' };
    }
}

async function copyToClipboard(target) {
    let textToCopy = '';
    if (target === 'mqtt-user') {
        textToCopy = credentials.value.user;
    } else if (target === 'mqtt-pass') {
        textToCopy = credentials.value.pass;
    } else {
        copyError.value = true;
        setTimeout(() => { copyError.value = false; }, 2000);
        return;
    }

    if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
            await navigator.clipboard.writeText(textToCopy);
            copySuccess.value = true;
            setTimeout(() => { copySuccess.value = false; }, 2000);
        } catch (err) {
            copyError.value = true;
            setTimeout(() => { copyError.value = false; }, 2000);
        }
    } else { // Fallback for older browsers
        const tempInput = document.createElement('input');
        tempInput.value = textToCopy;
        document.body.appendChild(tempInput);
        tempInput.select();
        try {
            document.execCommand('copy');
            copySuccess.value = true;
            setTimeout(() => { copySuccess.value = false; }, 2000);
        } catch (err) {
            copyError.value = true;
            setTimeout(() => { copyError.value = false; }, 2000);
        } finally {
            document.body.removeChild(tempInput);
        }
    }
}

function closeDialog() {
    emit('input', false);
    emit('dialog-closed');
    resetState();
}

function resetState() {
    credentials.value = null;
    copySuccess.value = false;
    copyError.value = false;
    showPassword.value = false;
}
</script>